import type { CreateItemRequest, Item, PlanDay, PlanLoose, Trip } from '@itin/shared'
import { formatZoneTime, zoneCityLabel } from '@itin/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { formatDate, useLocale, useT } from '../lib/i18n.js'
import { CopyDayButton } from './CopyDayButton.js'
import { ItemForm, KIND_GLYPH } from './ItemForm.js'
import { SavedPlacePicker } from './SavedPlacePicker.js'
import { SnippetPicker } from './SnippetPicker.js'
import {
  Button,
  Card,
  ErrorNotice,
  ErrorText,
  Field,
  GuidanceNote,
  Input,
  TextArea,
} from './ui.js'

/**
 * The advisor's side of the itinerary.
 *
 * One day per section, each a list of things in the order they happen, with one
 * "Add" that opens one form. What it replaced asked Ana to decide up front
 * whether the hotel was a "place" or a "booking" — two buttons, two forms, and
 * a wrong answer that could not be undone without deleting and retyping.
 *
 * The day's long text box is gone too. It was the only place a day could be
 * described, so it grew into a wall carrying addresses, times and phone numbers
 * that the product should have held as fields. Those are on the items now, and
 * what is left is one line for the day itself.
 */
export function PlanEditor({ trip }: { trip: Trip }) {
  const t = useT()
  const queryClient = useQueryClient()

  const plan = useQuery({ queryKey: ['plan', trip.id], queryFn: () => api.getPlan(trip.id) })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['plan', trip.id] })

  return (
    <Card testId="plan-card" title={t('plan.title')}>
      <div className="flex flex-col gap-5">
        {plan.isError && <ErrorNotice error={plan.error} />}
        {plan.isPending && <p className="text-sm text-slate-500">{t('common.loading')}</p>}

        {plan.data?.days.map((day, index) => (
          <DayEditor
            key={day.id}
            day={day}
            index={index}
            trip={trip}
            // The day sections are identical, so a field's explanation is shown
            // once rather than nine times. Repeating it down the page is how
            // somebody decides the explanations are noise and turns off the
            // twenty that were worth reading.
            explain={index === 0}
            onChange={invalidate}
          />
        ))}

        {plan.data && plan.data.loose.items.length > 0 && (
          <section className="rounded-md border border-dashed border-slate-300 p-4">
            <h3 className="label-caps text-slate-500">{t('plan.unscheduled')}</h3>
            <LooseList trip={trip} loose={plan.data.loose} onChange={invalidate} />
          </section>
        )}
      </div>
    </Card>
  )
}

function DayEditor({
  day,
  index,
  trip,
  explain,
  onChange,
}: {
  day: PlanDay
  index: number
  trip: Trip
  explain: boolean
  onChange(): Promise<void> | void
}) {
  const t = useT()
  const { locale } = useLocale()
  const queryClient = useQueryClient()

  /**
   * A day opens when it has something on it, and stays shut when it does not.
   *
   * Nine empty days each rendering two inputs and three buttons made the trip
   * page four thousand pixels of mostly-nothing, and the day Ana was actually
   * working on was lost in it. Shut, a day is one line she can scan; open, it
   * is a form. Clicking the header toggles it, and adding to a day opens it.
   */
  const [open, setOpen] = useState(day.items.length > 0)
  const [adding, setAdding] = useState(false)
  const [pickingSaved, setPickingSaved] = useState(false)
  const [pickingSnippet, setPickingSnippet] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [title, setTitle] = useState(day.title ?? '')
  const [notes, setNotes] = useState(day.notes ?? '')
  /** Open on a day with nothing written about it yet — see where it renders. */
  const [fieldsOpen, setFieldsOpen] = useState(!day.title && !day.notes)

  const titleDirty = title !== (day.title ?? '')
  const notesDirty = notes !== (day.notes ?? '')

  const saveDay = useMutation({
    mutationFn: (patch: { title?: string | null; notes?: string | null }) =>
      api.updateDay(day.id, patch),
    onSuccess: onChange,
  })

  const create = useMutation({
    mutationFn: (input: CreateItemRequest) =>
      api.createItem(trip.id, { ...input, dayId: day.id }),
    async onSuccess() {
      await onChange()
      setAdding(false)
    },
  })

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateItemRequest }) =>
      api.updateItem(id, input),
    async onSuccess() {
      await onChange()
      setEditingId(null)
    },
  })

  const remove = useMutation({ mutationFn: api.deleteItem, onSuccess: onChange })

  const saved = useQuery({
    queryKey: ['savedPlaces'],
    queryFn: api.listSavedPlaces,
    enabled: pickingSaved,
  })

  const addFromLibrary = useMutation({
    mutationFn: (savedPlaceId: string) =>
      api.addSavedPlaceToTrip(trip.id, { savedPlaceId, dayId: day.id }),
    async onSuccess() {
      await onChange()
      setPickingSaved(false)
    },
  })

  const snippets = useQuery({
    queryKey: ['snippets'],
    queryFn: api.listSnippets,
    enabled: pickingSnippet,
  })

  /**
   * Puts one of Ana's texts into this day's note, as a copy.
   *
   * The pending note is flushed FIRST, and awaited. The textarea saves on blur,
   * so clicking the button already fires an update — but that mutation is not
   * awaited, so it races the append: if the append lands first it reads the old
   * note, and the blur-save then overwrites the result with what was in the box
   * before. Sending the same value twice is harmless; losing the paragraph she
   * just inserted is not.
   */
  const insertSnippet = useMutation({
    mutationFn: async (snippetId: string) => {
      if (notesDirty) await api.updateDay(day.id, { notes: notes.trim() || null })
      return api.insertSnippetIntoDay(day.id, { snippetId })
    },
    async onSuccess(updated) {
      // The server owns the result, so the box shows what was actually stored
      // rather than what the client guessed it would be.
      setNotes(updated.notes ?? '')
      await onChange()
      setPickingSnippet(false)
    },
  })

  const keep = useMutation({
    mutationFn: (item: Item) =>
      api.saveTripPlace({
        placeId: item.id,
        destination: zoneCityLabel(trip.timezone, locale),
        kind: 'other',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['savedPlaces'] }),
  })

  return (
    <section className="rounded-md border border-slate-200">
      {/*
        Number, then the day's name over its date, then the count and the caret.
        Laid out as three columns rather than as one wrapping baseline row: at
        390px the old version broke between the title and the date and put the
        caret on a line of its own, so a shut day cost three lines and nine of
        them filled a screen before any content did.
      */}
      <button
        type="button"
        data-testid="day-header"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 border-b border-slate-100 bg-[#fdfcf9] px-4 py-3 text-left transition-colors hover:bg-[#faf6ee]"
      >
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-navy text-xs text-cream">
          {index + 1}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <h3 className="truncate text-lg leading-tight sm:text-xl">
            {day.title || <span className="text-slate-400">{t('day.untitled')}</span>}
          </h3>
          <span className="text-xs text-slate-500 sm:text-sm">{formatDate(day.date, locale)}</span>
        </span>
        <span className="flex shrink-0 items-center gap-2 text-xs text-slate-400">
          {/* "1 coisas" is the kind of thing that makes a product feel unfinished. */}
          {day.items.length === 1 && t('day.itemCountOne')}
          {day.items.length > 1 && t('day.itemCount', { count: day.items.length })}
          <span aria-hidden="true">{open ? '▾' : '▸'}</span>
        </span>
      </button>

      {!open && (
        <div className="px-4 py-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setOpen(true)
              setAdding(true)
            }}
          >
            {t('item.addFirst')}
          </Button>
        </div>
      )}

      <div
        data-testid={open ? 'day-body-open' : 'day-body-shut'}
        className={`flex-col gap-4 p-4 ${open ? 'flex' : 'hidden'}`}
      >
        {/*
          The day's own two fields, folded once the day has a name.

          They were the first thing inside every open day, so on a phone the
          screenful under the date was two inputs and their explanation — and
          the things actually on the day started below it. They are worth
          prompting for on a day that has no name yet, and worth getting out of
          the way on one that does; the header already shows the name, so
          nothing is hidden that was not already on screen.
        */}
        {!fieldsOpen && (
          <div>
            <Button variant="ghost" size="sm" onClick={() => setFieldsOpen(true)}>
              {t('day.editDay')}
            </Button>
          </div>
        )}

        {fieldsOpen && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('day.field.title')}>
                <Input
                  value={title}
                  maxLength={120}
                  onChange={(event) => setTitle(event.target.value)}
                  onBlur={() => {
                    if (titleDirty) saveDay.mutate({ title: title.trim() || null })
                  }}
                />
              </Field>
              <Field label={t('day.notesShort')}>
                {/*
                  A textarea, not an input, despite this being "one line" now.
                  Days migrated from TREK carry long notes, and a single-line
                  input hides all but the first forty characters of something Ana
                  wrote — which looks like data loss even though nothing was.
                */}
                <TextArea
                  value={notes}
                  rows={2}
                  onChange={(event) => setNotes(event.target.value)}
                  onBlur={() => {
                    if (notesDirty) saveDay.mutate({ notes: notes.trim() || null })
                  }}
                />
              </Field>
            </div>
            {/*
              Under the box it writes into, not up with the day's other actions.
              The library is only worth having where she is already typing — the
              same argument that moved the places picker inside the day.
            */}
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPickingSnippet((v) => !v)}
              >
                {t('snippets.addFrom')}
              </Button>
            </div>
            {insertSnippet.isError && <ErrorNotice error={insertSnippet.error} />}
            {pickingSnippet && (
              <SnippetPicker
                snippets={snippets.data ?? []}
                loading={snippets.isPending}
                here={zoneCityLabel(trip.timezone, locale)}
                // Also while a note save is in flight. The blur-save fired by
                // clicking into this picker is NOT awaited, so a pick issued
                // alongside it could have its result overwritten by a PATCH
                // carrying what was in the box a moment before.
                busy={insertSnippet.isPending || saveDay.isPending}
                onPick={(entry) => insertSnippet.mutate(entry.id)}
                onCancel={() => setPickingSnippet(false)}
              />
            )}
            {explain && (
              <GuidanceNote keys={['day.field.title.guidance', 'day.notesShort.guidance']} />
            )}
          </>
        )}

        {day.items.length === 0 && !adding && (
          <p className="text-sm text-slate-500">{t('day.empty')}</p>
        )}

        {day.items.length > 0 && (
          <ul className="flex flex-col divide-y divide-slate-100">
            {day.items.map((item) =>
              editingId === item.id ? (
                <li key={item.id} className="py-3">
                  <ItemForm
                    trip={trip}
                    dayDate={day.date}
                    existing={item}
                    busy={update.isPending}
                    error={update.error}
                    onSubmit={(input) => update.mutate({ id: item.id, input })}
                    onCancel={() => setEditingId(null)}
                  />
                </li>
              ) : (
                <ItemRow
                  key={item.id}
                  item={item}
                  trip={trip}
                  busy={keep.isPending}
                  onEdit={() => setEditingId(item.id)}
                  onKeep={() => keep.mutate(item)}
                  onRemove={() => {
                    if (confirm(t('item.removeConfirm', { name: item.title }))) {
                      remove.mutate(item.id)
                    }
                  }}
                />
              ),
            )}
          </ul>
        )}

        {(remove.isError || keep.isError) && (
          <ErrorNotice error={remove.error ?? keep.error} />
        )}

        {adding && (
          <ItemForm
            trip={trip}
            dayDate={day.date}
            busy={create.isPending}
            error={create.error}
            onSubmit={(input) => create.mutate(input)}
            onCancel={() => setAdding(false)}
          />
        )}

        {pickingSaved && (
          <SavedPlacePicker
            places={saved.data ?? []}
            here={zoneCityLabel(trip.timezone, locale)}
            busy={addFromLibrary.isPending}
            onPick={(id) => addFromLibrary.mutate(id)}
            onCancel={() => setPickingSaved(false)}
          />
        )}

        {!adding && (
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
              {day.items.length === 0 ? t('item.addFirst') : t('item.add')}
            </Button>
            <Button
              variant="quiet"
              size="sm"
              onClick={() => setPickingSaved((v) => !v)}
            >
              {t('savedPlaces.addFrom')}
            </Button>
            <CopyDayButton tripId={trip.id} dayId={day.id} onCopied={onChange} />
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * One line of a day, as Ana scans it: time, glyph, what it is.
 *
 * The whole row is the edit button.
 *
 * It used to carry three 32px controls — edit, keep, remove — laid out beside
 * the title. On a laptop that is a tidy row; on a phone it is three targets
 * inside one thumb-width, with the destructive one on the end. Editing is the
 * thing Ana does to a row constantly and deleting is the thing she does rarely
 * and cannot undo, so they should not be the same size and a hand's breadth
 * apart. Now the row itself opens the editor, and the two rarer actions live
 * behind a menu that has to be opened on purpose.
 */
function ItemRow({
  item,
  trip,
  busy,
  onEdit,
  onKeep,
  onRemove,
}: {
  item: Item
  trip: Trip
  busy: boolean
  onEdit(): void
  onKeep(): void
  onRemove(): void
}) {
  const t = useT()
  const { locale } = useLocale()
  const [menuOpen, setMenuOpen] = useState(false)
  const zone = item.timezone ?? trip.timezone
  const elsewhere = zone !== trip.timezone

  return (
    <li className="flex flex-col">
      <div className="flex items-start gap-x-2">
        {/*
          A button, not a div with an onClick: it has to be reachable by keyboard
          and announced as something that does a thing. `text-left` because a
          button centres its content by default and this one is a paragraph.
        */}
        <button
          type="button"
          onClick={onEdit}
          aria-label={t('item.editNamed', { name: item.title })}
          className="-mx-2 flex min-w-0 flex-1 items-start gap-x-3 rounded-md px-2 py-2.5 text-left transition-colors hover:bg-[#faf6ee] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-gold"
        >
          <span className="w-12 shrink-0 pt-0.5 text-sm tabular-nums text-slate-500">
            {item.startsAt ? formatZoneTime(zone, locale, new Date(item.startsAt)) : '—'}
          </span>
          <span aria-hidden="true" className="shrink-0 pt-0.5">
            {KIND_GLYPH[item.kind]}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="font-medium text-slate-900">{item.title}</span>
            {(item.address || item.confirmationRef || elsewhere) && (
              <span className="text-xs text-slate-500">
                {elsewhere && (
                  <span className="mr-1.5 rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-900">
                    {t('plan.localTimeIn', { place: zoneCityLabel(zone, locale) })}
                  </span>
                )}
                {item.address}
                {item.address && item.confirmationRef ? ' · ' : ''}
                {item.confirmationRef}
              </span>
            )}
            {item.notes && (
              // Two lines at most. A long note is for the traveller to read on
              // their page, not for Ana to scroll past nine times while working.
              <span className="line-clamp-2 text-xs text-slate-500">{item.notes}</span>
            )}
          </span>
        </button>

        <button
          type="button"
          data-testid="item-menu"
          aria-expanded={menuOpen}
          aria-label={t('item.moreActions', { name: item.title })}
          onClick={() => setMenuOpen((v) => !v)}
          className="mt-0.5 flex size-11 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-gold sm:size-9"
        >
          <span aria-hidden="true">⋯</span>
        </button>
      </div>

      {menuOpen && (
        <div className="flex flex-wrap gap-2 pb-3 pl-14">
          <Button
            variant="quiet"
            size="sm"
            disabled={busy}
            onClick={() => {
              onKeep()
              setMenuOpen(false)
            }}
          >
            {t('savedPlaces.keep')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            aria-label={t('item.removeConfirm', { name: item.title })}
            onClick={() => {
              onRemove()
              setMenuOpen(false)
            }}
          >
            {t('common.remove')}
          </Button>
        </div>
      )}
    </li>
  )
}

/** On the trip, not yet on a day. Removable, and never silently dropped. */
function LooseList({
  trip,
  loose,
  onChange,
}: {
  trip: Trip
  loose: PlanLoose
  onChange(): Promise<void> | void
}) {
  const t = useT()
  const remove = useMutation({ mutationFn: api.deleteItem, onSuccess: onChange })

  return (
    <>
      <ul className="mt-2 flex flex-col divide-y divide-slate-100">
        {loose.items.map((item) => (
          <li key={item.id} className="flex items-center justify-between gap-2 py-2 text-sm">
            <span className="min-w-0">
              <span aria-hidden="true" className="mr-2">
                {KIND_GLYPH[item.kind]}
              </span>
              {item.title}
            </span>
            <Button
              variant="danger"
              size="sm"
              aria-label={t('item.removeConfirm', { name: item.title })}
              onClick={() => {
                if (confirm(t('item.removeConfirm', { name: item.title }))) {
                  remove.mutate(item.id)
                }
              }}
            >
              {t('common.remove')}
            </Button>
          </li>
        ))}
      </ul>
      {remove.isError && <ErrorText>{t('error.internal')}</ErrorText>}
      <span className="sr-only">{trip.id}</span>
    </>
  )
}
